import React, { useEffect, useState } from 'react';
import Layout from '../common/Layout';
import ListPagination from '../common/ListPagination';
import { useAppData } from '../../context/AppDataContext';
import { useAuth } from '../../context/AuthContext';
import { CallReportResponse, CallReportRow, CallReportSummaryRow, CallReportStatusSummaryRow } from '../../types';
import { callReportService, CallReportFilters } from '../../services/callReportService';
import { formatDate, formatDateTime, getPastDateISO, getTodayISO } from '../../utils/dateUtils';
import { useCallOutcomeOptions } from '../../hooks/useCallOutcomeOptions';
import { useCallStatusOptions } from '../../hooks/useCallStatusOptions';
import { getSettingColorClass } from '../../utils/settingColors';

type ReportType = 'lead' | 'tender' | 'customer';

interface CallReportPageProps {
  title: string;
  reportType: ReportType;
}

const SERVICE_BY_TYPE: Record<ReportType, (filters?: CallReportFilters) => Promise<CallReportResponse>> = {
  lead: callReportService.getLeadCalls,
  tender: callReportService.getTenderCalls,
  customer: callReportService.getCustomerCalls,
};

const EXEC_SUMMARY_BLOCK_CLASSES = [
  'bg-gradient-to-r from-yellow-50 to-yellow-100 border-yellow-200',
  'bg-gradient-to-r from-blue-50 to-blue-100 border-blue-200',
  'bg-gradient-to-r from-green-50 to-green-100 border-green-200',
  'bg-gradient-to-r from-purple-50 to-purple-100 border-purple-200',
  'bg-gradient-to-r from-rose-50 to-rose-100 border-rose-200',
  'bg-gradient-to-r from-cyan-50 to-cyan-100 border-cyan-200',
];

const CallReportPage: React.FC<CallReportPageProps> = ({ title, reportType }) => {
  const { user } = useAuth();
  const { options: callOutcomeOptions } = useCallOutcomeOptions();
  const { options: callStatusOptions, loading: callStatusOptionsLoading } = useCallStatusOptions();
  const { users, refreshUsersForDropdown } = useAppData();
  const [rows, setRows] = useState<CallReportRow[]>([]);
  const [summary, setSummary] = useState<CallReportSummaryRow[]>([]);
  const [statusSummary, setStatusSummary] = useState<CallReportStatusSummaryRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [calledBy, setCalledBy] = useState<number | ''>('');
  const [searchText, setSearchText] = useState('');
  const [callStatusId, setCallStatusId] = useState<number | ''>('');
  const [callOutcome, setCallOutcome] = useState('');
  const [dateFrom, setDateFrom] = useState(getPastDateISO(6));
  const [dateTo, setDateTo] = useState(getTodayISO());
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(50);
  const [total, setTotal] = useState(0);

  // Run once on mount to load Sales Executive dropdown options.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    refreshUsersForDropdown();
  }, []);

  const loadReport = async (
    nextFilters: CallReportFilters,
    nextPage: number,
    nextPerPage: number
  ) => {
    try {
      setLoading(true);
      setError('');
      const fn = SERVICE_BY_TYPE[reportType];
      const result = await fn({
        search: nextFilters.search,
        called_by: nextFilters.called_by,
        call_status_id: nextFilters.call_status_id,
        call_outcome: nextFilters.call_outcome,
        date_from: nextFilters.date_from,
        date_to: nextFilters.date_to,
        page: nextPage,
        per_page: nextPerPage,
      });
      setRows(result.data || []);
      setSummary(result.summary || []);
      setStatusSummary(result.status_summary || []);
      setTotal(result.total || 0);
    } catch (err: any) {
      setError(err.response?.data?.message || err.response?.data?.error || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  // We intentionally fetch here for page/per-page/report type changes; Search/Clear trigger explicit fetches.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (
      user?.role !== 'org_admin' &&
      user?.role !== 'manager' &&
      user?.role !== 'executive'
    ) {
      return;
    }
    loadReport(
      {
        called_by:
          user?.role === 'executive' && user?.id != null ? user.id : calledBy === '' ? undefined : calledBy,
        search: searchText.trim() || undefined,
        call_status_id: callStatusId === '' ? undefined : callStatusId,
        call_outcome: callOutcome || undefined,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
      },
      page,
      perPage
    );
  }, [page, perPage, reportType, user?.role, user?.id]);

  const handleSearch = () => {
    setPage(1);
    loadReport(
      {
        called_by:
          user?.role === 'executive' && user?.id != null ? user.id : calledBy === '' ? undefined : calledBy,
        search: searchText.trim() || undefined,
        call_status_id: callStatusId === '' ? undefined : callStatusId,
        call_outcome: callOutcome || undefined,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
      },
      1,
      perPage
    );
  };

  const handleClear = () => {
    setCalledBy('');
    setSearchText('');
    setCallStatusId('');
    setCallOutcome('');
    setDateFrom(getPastDateISO(6));
    setDateTo(getTodayISO());
    setPage(1);
    setPerPage(50);
    loadReport(
      {
        called_by:
          user?.role === 'executive' && user?.id != null ? user.id : undefined,
        search: undefined,
        call_status_id: undefined,
        call_outcome: undefined,
        date_from: getPastDateISO(6),
        date_to: getTodayISO(),
      },
      1,
      50
    );
  };


  const canViewReports =
    user?.role === 'org_admin' || user?.role === 'manager' || user?.role === 'executive';

  if (!canViewReports) {
    return (
      <Layout>
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded text-sm">
          You do not have permission to view this report.
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">{title}</h1>
        </div>

        <div className="bg-white p-4 rounded-lg shadow-sm">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-8 gap-3 items-end">
            {user?.role !== 'executive' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Sales Executive</label>
              <select
                value={calledBy}
                onChange={(e) => setCalledBy(e.target.value ? Number(e.target.value) : '')}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              >
                <option value="">All Executives</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name}
                  </option>
                ))}
              </select>
            </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Call Status</label>
              <select
                value={callStatusId}
                onChange={(e) => setCallStatusId(e.target.value ? Number(e.target.value) : '')}
                disabled={callStatusOptionsLoading}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm disabled:opacity-60"
              >
                <option value="">{callStatusOptionsLoading ? 'Loading…' : 'All Status'}</option>
                {callStatusOptions.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Call Outcome</label>
              <select
                value={callOutcome}
                onChange={(e) => setCallOutcome(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              >
                <option value="">All Outcomes</option>
                {callOutcomeOptions.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">To Date</label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {reportType === 'tender' ? 'Party Name' : 'Name'}
              </label>
              <input
                type="text"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                placeholder={reportType === 'tender' ? 'Search by party name' : 'Search by name'}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              />
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleSearch}
                className="bg-yellow-500 text-black-950 px-4 py-2 rounded-md hover:bg-yellow-600 text-sm font-bold"
              >
                Search
              </button>
              <button
                onClick={handleClear}
                className="bg-gray-200 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-300 text-sm font-medium"
              >
                Clear
              </button>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow-sm">
          <h2 className="text-sm font-semibold text-gray-700 mb-2">Executive Summary</h2>
          {summary.length === 0 ? (
            <p className="text-sm text-gray-500">No call summary available for selected filters.</p>
          ) : (
            <div className="flex flex-wrap items-stretch gap-2">
              {summary.map((s, idx) => (
                <div
                  key={s.called_by}
                  className={`min-w-[170px] rounded-md border px-3 py-1.5 ${EXEC_SUMMARY_BLOCK_CLASSES[idx % EXEC_SUMMARY_BLOCK_CLASSES.length]}`}
                >
                  <div className="text-sm text-gray-900 font-semibold truncate" title={s.called_by_name}>
                    {s.called_by_name}
                  </div>
                  <div className="mt-0.5 text-xs text-gray-700">
                    <span>Total Calls: </span>
                    <span className="font-bold text-gray-900">{s.total_calls}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white p-4 rounded-lg shadow-sm">
          <h2 className="text-sm font-semibold text-gray-700 mb-2">Call Status Summary</h2>
          {statusSummary.length === 0 ? (
            <p className="text-sm text-gray-500">No status summary available for selected filters.</p>
          ) : (
            <div className="flex flex-wrap items-center gap-2">
              {statusSummary.map((s) => (
                <div
                  key={s.call_status_id ?? s.call_status}
                  className={`inline-flex items-center gap-1.5 border rounded-full px-3 py-1 text-xs ${getSettingColorClass(s.call_status_color_code)}`}
                  title={`${s.call_status}: ${s.total_calls} calls`}
                >
                  <span className="font-medium">{s.call_status}</span>
                  <span className="opacity-80">{s.total_calls}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {error && <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded text-sm">{error}</div>}

        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Call Date/Time</th>
                  <th className="px-3 py-2 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">
                    {reportType === 'tender' ? 'Party Name' : 'Entity Name'}
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Called By</th>
                  <th className="px-3 py-2 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Call Status</th>
                  <th className="px-3 py-2 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Call Outcome</th>
                  <th className="px-3 py-2 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Next Call Date</th>
                  <th className="px-3 py-2 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Notes</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan={7} className="px-3 py-6 text-center text-sm text-gray-500">
                      Loading...
                    </td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-3 py-6 text-center text-sm text-gray-500">
                      No records found.
                    </td>
                  </tr>
                ) : (
                  rows.map((r) => (
                    <tr key={r.id} className="hover:bg-gray-50">
                      <td className="px-3 py-2 text-xs text-gray-700">
                        {r.call_time ? formatDateTime(`${r.call_date} ${r.call_time}`) : formatDate(r.call_date)}
                      </td>
                      <td className="px-3 py-2 text-xs text-gray-700">{r.entity_name || '-'}</td>
                      <td className="px-3 py-2 text-xs text-gray-700">{r.called_by_name || '-'}</td>
                      <td className="px-3 py-2 text-xs text-gray-700">{r.call_status || '-'}</td>
                      <td className="px-3 py-2 text-xs text-gray-700">{r.call_outcome || '-'}</td>
                      <td className="px-3 py-2 text-xs text-gray-700">{r.next_call_date ? formatDateTime(r.next_call_date) : '-'}</td>
                      <td className="px-3 py-2 text-xs text-gray-700 max-w-[20rem] truncate" title={r.notes || undefined}>
                        {r.notes || '-'}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <ListPagination
            page={page}
            perPage={perPage}
            total={total}
            onPageChange={setPage}
            onPerPageChange={setPerPage}
          />
        </div>
      </div>
    </Layout>
  );
};

export default CallReportPage;

